import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getSuperAdminUserActivities,
  getSuperAdminUserActivitiesCount,
  SuperAdminUserActivity as SuperAdminUserActivityItem
} from 'Src/helpers/service/superadmin/userActivity';

function formatDateTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function getUserName(item: SuperAdminUserActivityItem): string {
  if (!item.user) return '';
  const { firstName, lastName } = item.user;
  return [firstName, lastName].filter(Boolean).join(' ');
}

function getIpAddress(item: SuperAdminUserActivityItem): string {
  return (item.httpHeaders && item.httpHeaders['x-real-ip']) || '';
}

function getStatusBadgeClass(status?: number): string {
  if (!status) return 'badge badge-secondary';
  if (status < 300) return 'badge badge-success';
  if (status < 400) return 'badge badge-info';
  if (status < 500) return 'badge badge-warning';
  return 'badge badge-danger';
}

function getMethodBadgeClass(method?: string): string {
  switch (method) {
    case 'GET':
      return 'badge badge-primary';
    case 'POST':
      return 'badge badge-success';
    case 'PUT':
      return 'badge badge-warning';
    case 'DELETE':
      return 'badge badge-danger';
    default:
      return 'badge badge-secondary';
  }
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'user',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: (item: SuperAdminUserActivityItem) => getUserName(item)
  },
  {
    text: 'Email',
    field: 'userEmail',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['userEmail']
  },
  {
    text: 'IP Address',
    field: 'ipAddress',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: (item: SuperAdminUserActivityItem) => getIpAddress(item)
  },
  {
    text: 'Method',
    field: 'httpVerb',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: (item: SuperAdminUserActivityItem) => item.httpVerb || ''
  },
  {
    text: 'URL',
    field: 'httpUrl',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: (item: SuperAdminUserActivityItem) => item.httpUrl || ''
  },
  {
    text: 'Status',
    field: 'httpStatusCode',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: (item: SuperAdminUserActivityItem) => (item.httpStatusCode != null ? String(item.httpStatusCode) : '')
  },
  {
    text: 'Finished',
    field: 'finishedAt',
    isSort: true,
    type: FILTER_TYPE.DATETIME_INTERVAL,
    accessor: (item: SuperAdminUserActivityItem) => formatDateTime(item.finishedAt)
  }
];

interface Props {
  history: History;
  location: Location;
}

interface State {
  items: SuperAdminUserActivityItem[];
  currentPage: number;
  selectedItems: SuperAdminUserActivityItem[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
}

export class SuperAdminUserActivity extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems();
    }
  }

  setItems() {
    const { history } = this.props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'finishedAt DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([getSuperAdminUserActivitiesCount(where), getSuperAdminUserActivities(serverQueryFilter)])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load user activity', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load user activity. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(si => si.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getServerFieldSectionWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getSuperAdminUserActivities({ where })
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(si => si.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all user activities', error);
        this.setState({ isLoading: false, errorMessage: 'Can not select all records. Please try again.' });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      nextFilters = { ...nextFilters, [filterField]: filterValue };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  renderCustomCell = (column: ColumnDefinition, item: SuperAdminUserActivityItem): React.ReactNode | null => {
    if (column.field === 'httpVerb') {
      const method = item.httpVerb || '';
      return <span className={getMethodBadgeClass(method)}>{method}</span>;
    }

    if (column.field === 'httpStatusCode') {
      const status = item.httpStatusCode;
      return status != null ? <span className={getStatusBadgeClass(status)}>{status}</span> : null;
    }

    if (column.field === 'ipAddress') {
      return <span>{getIpAddress(item)}</span>;
    }

    if (column.field === 'httpUrl') {
      const url = item.httpUrl || '';
      return (
        <span
          title={url}
          style={{
            maxWidth: 350,
            display: 'inline-block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle'
          }}
        >
          {url}
        </span>
      );
    }

    return null;
  };

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-1">User Activity</h2>
            <div className="text-muted">HTTP request log for all users.</div>
          </div>
          <div>
            <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
              Refresh
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={[]}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
        />
      </div>
    );
  }
}
